import { useEffect, useState } from "react";
import { Trophy, Crown, Medal } from "@phosphor-icons/react";
import api from "@/lib/api";

const PERIODS = [
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "all", label: "All Time" },
];

function rankIcon(rank) {
  if (rank === 1) return <Crown size={20} weight="fill" className="text-[#F59E0B]" />;
  if (rank === 2) return <Medal size={20} weight="fill" className="text-zinc-400" />;
  if (rank === 3) return <Medal size={20} weight="fill" className="text-amber-700" />;
  return <span className="text-zinc-600 font-mono text-sm">#{rank}</span>;
}

export default function Leaderboard() {
  const [period, setPeriod] = useState("week");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/leaderboard?period=${period}`);
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [period]);

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-mono">// Ranks</div>
        <h1 className="font-display text-4xl md:text-5xl font-black uppercase">Leaderboard</h1>
        <p className="text-zinc-500 mt-1 text-sm">Ranked by total volume lifted.</p>
      </div>

      <div className="flex border border-[#27272A] w-fit">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-colors ${
              period === p.key ? "bg-[#FF3B30] text-white" : "text-zinc-400 hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#27272A] p-12 text-center">
          <Trophy size={40} weight="bold" className="text-zinc-700 mx-auto mb-4" />
          <div className="font-display text-2xl font-bold uppercase text-zinc-400 mb-2">No data</div>
          <p className="text-zinc-500 text-sm">No workouts in this period yet.</p>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#121212]">
          {rows.map((r) => (
            <div
              key={r.user?.id || r.rank}
              data-testid={`leaderboard-row-${r.rank}`}
              className="flex items-center gap-4 p-4 border-b border-[#27272A] last:border-b-0 hover:bg-[#1E1E1E] transition-colors"
            >
              <div className="w-10 flex justify-center">{rankIcon(r.rank)}</div>
              <div className="w-10 h-10 bg-[#27272A] flex items-center justify-center flex-shrink-0">
                <span className="font-display font-bold text-white">
                  {(r.user?.name || "?").charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold uppercase tracking-tight truncate">{r.user?.name || "Unknown"}</div>
                <div className="text-xs text-zinc-500 font-mono">@{r.user?.username}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-black text-[#FF3B30]">
                  {Math.round(r.total_volume).toLocaleString()}
                  <span className="text-xs text-zinc-500 font-mono ml-1">kg</span>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  {r.workout_count} workout{r.workout_count === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
